import { Form } from "antd";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { useSelector } from "react-redux";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import {
  getOptionsFromRefList,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";
import dayjs from "dayjs";
import DynamicForm from "@/common/DynamicForm";
import { BookingsSectionTitle } from "@/components/BookingsDetails/styles";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";

// Fields here go disabled whenever viewOnly is set (a committed, read-only
// transaction) or the page's own Modify mode is off — antd's default
// disabled-text color is very low-contrast against this form's light grey
// background, hard to read even though the value is genuinely there (not
// missing). Darkens it without changing anything about which fields are
// actually editable. -webkit-text-fill-color is needed alongside color —
// Safari/Chrome apply it separately for a disabled/readonly <input>.
const ReadableDisabledFields = styled.div`
  .ant-input[disabled],
  .ant-input-disabled,
  textarea.ant-input[disabled] {
    color: rgba(0, 0, 0, 0.75) !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.75) !important;
  }
  .ant-select-disabled .ant-select-selector .ant-select-selection-item {
    color: rgba(0, 0, 0, 0.75) !important;
  }
`;

const CashAccountDetailForm = forwardRef(
  (
    { purchaseFormRef, startingPoint = "", initialData, viewOnly = false },
    ref,
  ) => {
    const [form] = Form.useForm(); // ← declared first

    const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);

    useEffect(() => {
      if (initialData) {
        console.log("initialData: ", initialData);
        form.setFieldsValue(initialData);
      } else {
        form.resetFields();
      }
    }, [initialData]);

    // -----------------------------------------------------------------------
    // CashAccountingNewTransactionsExtra — PCI's per-client dynamic "_U"
    // columns for this transaction (CashAccountingNewTransactionsInterface.asp
    // 's "Additional Transaction Information" section, shown right after
    // Transaction Details). Used both when adding a new Purchase (no
    // initialData, nothing to fetch) and when viewing/editing an existing
    // transaction (Details button — initialData carries a real
    // cashAccountingTransactionId).
    // -----------------------------------------------------------------------
    const [transactionExtraFields, setTransactionExtraFields] = useState([]);
    const [transactionExtraFieldsReady, setTransactionExtraFieldsReady] =
      useState(false);
    const transactionExtraTypeMapRef = useRef({});
    const transactionExtraIdRef = useRef(0);

    useEffect(() => {
      api
        .get(
          `${API_ENDPOINTS.getTableColumns}/CashAccountingNewTransactionsExtra`,
        )
        .then((response) => {
          const tableData = (response?.data || []).filter((d) =>
            d?.columnName?.endsWith("_U"),
          );
          const converted = convertApiFields(
            tableData,
            "CashAccountingNewTransactionsExtra",
          );
          const typeMap = {};
          converted.forEach((c) => {
            typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
          });
          transactionExtraTypeMapRef.current = typeMap;
          setTransactionExtraFields(converted);
          setTransactionExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error(
            "[CashAccountDetailForm] CashAccountingNewTransactionsExtra field defs fetch failed:",
            error,
          );
        });
    }, []);

    useEffect(() => {
      if (!transactionExtraFieldsReady) return;
      const id = initialData?.cashAccountingTransactionId;
      if (!isNonEmptyVariable(id)) {
        transactionExtraIdRef.current = 0;
        return;
      }

      fetchExtraTableRecord(
        "CashAccountingNewTransactionsExtra",
        "cashAccountingTransactionID",
        id,
      )
        .then((result) => {
          if (!result) return;
          transactionExtraIdRef.current =
            result.raw?.CashAccountingTransactionExtraID ?? 0;
          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = transactionExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] =
              isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          form.setFieldsValue(withDates);
        })
        .catch((error) => {
          console.error(
            "[CashAccountDetailForm] CashAccountingNewTransactionsExtra fetch failed:",
            error,
          );
        });
    }, [initialData?.cashAccountingTransactionId, transactionExtraFieldsReady]);

    const handleAmountKeyDown = (e) => {
      const { key, ctrlKey, metaKey } = e;
      const allowedControlKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Tab",
        "Enter",
        "Home",
        "End",
        "Escape",
        "CapsLock",
        "Shift",
        "Control",
      ];
      if (allowedControlKeys.includes(key) || ctrlKey || metaKey) return;
      if (key === "." && !e.target.value.includes(".")) return;
      if (!/^[0-9]$/.test(key)) {
        e.preventDefault();
      }
    };

    useImperativeHandle(purchaseFormRef, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      setFormValue: () => form.setFieldValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: () => form.validateFields(),
      transactionExtraFieldKeys: transactionExtraFields.map(
        (f) => f.columnName,
      ),
      transactionExtraTypeMap: transactionExtraTypeMapRef.current,
      transactionExtraID: transactionExtraIdRef.current,
    }));

    return (
      <ReadableDisabledFields>
        <FieldSecurityProvider
          entity={SCHEMA_NAME.CASH_ACCOUNTING_NEW_TRANSACTIONS}
          startingPoint={startingPoint}
          bookingsFieldsEnabled={bookingsFieldsEnabled && !viewOnly}
        >
          {viewOnly && (
            <div
              style={{
                backgroundColor: "#fff7e6",
                border: "1px solid #ffd591",
                borderRadius: "6px",
                padding: "10px 14px",
                marginBottom: "16px",
                color: "#874d00",
                fontSize: "13px",
              }}
            >
              This transaction has already been committed to the database. You
              cannot modify it. Click the Close Button to close this window.
            </div>
          )}
          <Form form={form} disabled={viewOnly}>
            <StyledSectionHeading>
              <span>
                Please enter the amount of the withdrawal from the account:
              </span>
            </StyledSectionHeading>

            <StyledGridRow gutter={16}>
              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="amount"
                  formItemProps={{
                    name: "amount",
                    label: "Amount",
                    rules: [{ required: true, message: "Amount is required" }],
                  }}
                >
                  <TextField prefix="$" onKeyDown={handleAmountKeyDown} />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="checkNumber"
                  formItemProps={{ name: "checkNumber", label: "Check #" }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="category" // ← lowercase
                  formItemProps={{
                    name: "category", // ← lowercase
                    label: "Category",
                    rules: [
                      { required: true, message: "Category is required" },
                    ],
                  }}
                >
                  <Select options={getOptionsFromRefList("Category")} />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>

            <StyledGridRow gutter={16}>
              <StyledGridCol lg={24}>
                <SecureFormItem
                  fieldName="transactionDetails"
                  formItemProps={{
                    name: "transactionDetails",
                    label: "Transaction Details",
                  }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>

            {transactionExtraFields.length > 0 && (
              <>
                <BookingsSectionTitle>
                  <span>Additional Transaction Information</span>
                </BookingsSectionTitle>
                <StyledGridRow gutter={16}>
                  {transactionExtraFields.map((sf, index) => (
                    <DynamicForm
                      key={index}
                      formAttributes={sf}
                      form={form}
                      bookingsFieldsEnabled={bookingsFieldsEnabled && !viewOnly}
                    />
                  ))}
                </StyledGridRow>
              </>
            )}
          </Form>
        </FieldSecurityProvider>
      </ReadableDisabledFields>
    );
  },
);

export default CashAccountDetailForm;
